/// <amd-module name="Wit/DeliveryPlans/Common/Services/DeliveryPlanShortcutService" />

import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { format } from "VSS/Core/Util/String";
import { Direction, IHotKeyEventType, IHotKeyEventService, IHotKeyEvent, IHotKeyEventData } from "Wit/Common/HotKeyEventService/IHotKeyEventService";

export interface IDeliveryPlanShortcutService extends IVssService {
    registerShortcuts: (
        toggleFilter: () => void,
        toggleFullScreen: () => void,
        openWorkItemDialog: (workItemId: number, onSave?: (workItem: any) => void) => void
    ) => void;
    unregisterShortcuts: () => void;
}

class DeliveryPlanShortcutService extends VssService implements IDeliveryPlanShortcutService {
    private shortcutService: IVssKeyboardShortcutService;

    public _serviceStart(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.shortcutService = this.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
    }

    public _serviceEnd() { }

    public registerShortcuts = (
        toggleFilter: () => void,
        toggleFullScreen: () => void,
        openWorkItemDialog: (workItemId: number, onSave?: (workItem: any) => void) => void
    ) => {

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["home"],
            description: Resources.SelectFirstItem,
            action: () => {
                const firstCard = document.querySelector(".wit-card") as HTMLElement | null;
                firstCard?.focus();
            }
        });

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["enter"],
            description: Resources.OpenItem,
            action: () => {
                const id = this.getFocusedWorkItemId();

                if (id) {
                    openWorkItemDialog(id);
                }
            }
        });

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["n"],
            description: Resources.NewItem,
            action: () => {
                const teamId = this.getFocusedWorkTeamId();
                const backlog = this.getFocusedWorkBacklog();
                const iterationId = this.getFocusedWorkIteration();
                if (!teamId || !backlog || !iterationId) {
                    const firstCard = document.querySelector(".add-new-item-iteration-button") as HTMLElement | null;
                    firstCard?.click();
                }
                
                this.triggerHotKeyEvent(IHotKeyEventType.NewWorkItem, {teamId, backlog, iterationId});
            }
        });

        
        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["mod+up"],
            description: Resources.MoveItemUp,
            action: () => {
                this.tryToMoveWorkItem(Direction.Up);
            }
        });

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["mod+down"],
            description: Resources.MoveItemDown,
            action: () => {
                this.tryToMoveWorkItem(Direction.Down);
            }
        });

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["mod+left"],
            description: Resources.MoveItemLeft,
            action: () => {
                this.tryToMoveWorkItem(Direction.Left);
            }
        });

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["mod+right"],
            description: Resources.MoveItemRight,
            action: () => {
                this.tryToMoveWorkItem(Direction.Right);
            }
        });
    

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["shift+left"],
            // description: Resources.DirectionLeft,
            description: format(Resources.ShiftCalendarButtonAriaLabel, Resources.DirectionLeft),
            action: () => {
                this.triggerHotKeyEvent(IHotKeyEventType.PanTimeline, {direction: Direction.Left});
            }
        });
        
        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["shift+right"],
            description:  format(Resources.ShiftCalendarButtonAriaLabel, Resources.DirectionRight), 
            action: () => {
                this.triggerHotKeyEvent(IHotKeyEventType.PanTimeline, {direction: Direction.Right});
            }
        });

        
        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["u"],
            description: Resources.CollapseAllTeamRows,
            action: () => {
                this.triggerHotKeyEvent(IHotKeyEventType.CollapseAllTeamRows);
            }
        });

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["o"],
            description: Resources.ExpandAllTeamRows,
            action: () => {
                this.triggerHotKeyEvent(IHotKeyEventType.ExpandAllTeamRows);
            }
        });

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["shift+pageup"],
            description: Resources.FocusOnNextTeam,
            action: () => {
                this.focusPreviousSwimlaneHeader();
            }
        });
    
        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["shift+pagedown"],
            description: Resources.FocusOnPreviousTeam,
            action: () => {
                this.focusNextSwimlaneHeader();
            }
        });

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["mod+shift+f"],
            description: Resources.ToggleFilter,
            action: () => {
                toggleFilter();

                this.triggerHotKeyEvent(IHotKeyEventType.FilterToggled);
            }
        });

        this.shortcutService.registerShortcut(Resources.DeliveryPlansHeader, {
            combos: ["z"],
            description: Resources.ToggleFullScreenMode,
            action: () => {
                toggleFullScreen();
            }
        });
    }

    public unregisterShortcuts = () => {
        this.shortcutService.removeShortcutGroup(Resources.DeliveryPlansHeader);
    };

    private getFocusedWorkTeamId = (): string | undefined => {
        const focusedElement = document.activeElement as HTMLElement | undefined;
        if (!(focusedElement?.classList.contains("wit-card")
            || focusedElement?.classList.contains("iteration-header"))) {
            return;
        }

        return focusedElement.dataset.team;
    }

    private getFocusedWorkBacklog = (): string | undefined => {
        const focusedElement = document.activeElement as HTMLElement | undefined;
        if (!(focusedElement?.classList.contains("wit-card")
            || focusedElement?.classList.contains("iteration-header"))) {
            return;
        }

        return focusedElement.dataset.backlog;
    }

    private getFocusedWorkIteration = (): string | undefined => {
        const focusedElement = document.activeElement as HTMLElement | undefined;
        if (!(focusedElement?.classList.contains("wit-card")
            || focusedElement?.classList.contains("iteration-header"))) {
            return;
        }

        return focusedElement.dataset.iteration;
    }

    private getFocusedWorkItemRow = (): number | undefined => {
        const focusedElement = document.activeElement as HTMLElement | undefined;
        if (!focusedElement?.classList.contains("wit-card")) {
            return;
        }

        const row = Number(focusedElement.dataset.row);
        if (!row) {
            return;
        }

        return row;
    }

    private getFocusedWorkItemId = (): number | undefined => {
        const focusedElement = document.activeElement as HTMLElement | undefined;
        if (!focusedElement?.classList.contains("wit-card")) {
            return;
        }

        const itemId = Number(focusedElement.dataset.itemid);
        if (!itemId || itemId <= 0) {
            return;
        }

        return itemId;
    }

    private tryToMoveWorkItem = (direction: Direction) => {
        const teamId = this.getFocusedWorkTeamId();
        const backlog = this.getFocusedWorkBacklog();
        const iterationId = this.getFocusedWorkIteration();
        const row = this.getFocusedWorkItemRow();
        const id = this.getFocusedWorkItemId();

        if (!teamId || !backlog || !iterationId || !row || !id) {
            return;
        }

        this.triggerHotKeyEvent(
            IHotKeyEventType.MoveWorkItem, 
            {
                direction: direction,
                workItemId: id,
                backlog, teamId, iterationId, row
            });
    }

    private triggerHotKeyEvent = (eventType: IHotKeyEvent, eventData?: IHotKeyEventData) => {
        const hotKeyEventService = this.pageContext.getService<IHotKeyEventService>("IHotKeyEventService");
        hotKeyEventService.fireEvent(eventData ?? {}, eventType);
    }

    private focusPreviousSwimlaneHeader = (): void => {
        const focusedSwimlane = this.getFocusedSwimlaneHeader();
        const swimlaneHeaders = Array.from(document.querySelectorAll<HTMLElement>(".team-header"));
        if (swimlaneHeaders.length === 0) {
            return;
        }
    
        if (focusedSwimlane) {
            // focus the previous swimlane
            const currentIndex = swimlaneHeaders.findIndex(header => header === focusedSwimlane);
            if (currentIndex > 0) {
                const nextHeader = swimlaneHeaders[currentIndex - 1];
                nextHeader.focus();
            }
        } else {
            // focus the first swimlane
            swimlaneHeaders[0].focus();
        }
    }
    
    /**
     * Focus the first swimlane header or the previous swimlane header if a swimlane header is currently focused
     */
    private focusNextSwimlaneHeader = (): void => {
        const focusedSwimlane = this.getFocusedSwimlaneHeader();
        const swimlaneHeaders = Array.from(document.querySelectorAll<HTMLElement>(".team-header"));
        if (swimlaneHeaders.length === 0) {
            return;
        }
    
        if (focusedSwimlane) {
            // focus the next swimlane
            const currentIndex = swimlaneHeaders.findIndex(header => header === focusedSwimlane);
            if (currentIndex < swimlaneHeaders.length - 1) {
                const nextHeader = swimlaneHeaders[currentIndex + 1];
                nextHeader.focus();
            }
        } else {
            // focus the last swimlane
            swimlaneHeaders[swimlaneHeaders.length - 1].focus();
        }
    }
    
    private getFocusedSwimlaneHeader = (): HTMLElement | null => {
        let currentElement = document.activeElement as HTMLElement | null;
        while (currentElement) {
            if (currentElement.classList.contains("team-header")) {
                break;
            } else {
                currentElement = currentElement.parentElement;
            }
        }
    
        return currentElement;
    }
}

Services.add("IDeliveryPlanShortcutService", { serviceFactory: DeliveryPlanShortcutService });
